import { useTranslate } from "@similarweb/i18n-provider";
import { Download, Refresh } from "@similarweb/icons";
import { CircularLoader, Tooltip } from "@similarweb/ui-components";

import type { SignalRecommenderFileStatus } from "../../types/model";
import { isDealSignalSucceeded } from "../../utils/dealSignalStatus";
import signalRecommenderI18nKeys from "../../utils/i18n";

import { DOWNLOAD_LOADER_OPTIONS, StyledDownloadButton } from "./styles";

type DealSignalFileActionProps = {
    readonly status: SignalRecommenderFileStatus;
    readonly isDownloading: boolean;
    /** Passed in rather than derived: the age test needs `created`, which this action never receives. */
    readonly isStalled?: boolean;
    readonly isRefreshing?: boolean;
    readonly describedById?: string;
    readonly onDownload: VoidFunction;
    readonly onRefresh: VoidFunction;
};

export const DealSignalFileAction = ({
    status,
    isDownloading,
    isStalled = false,
    isRefreshing = false,
    describedById,
    onDownload,
    onRefresh,
}: DealSignalFileActionProps) => {
    const { translate } = useTranslate();

    if (isDownloading || isRefreshing) {
        return (
            <StyledDownloadButton
                type="button"
                disabled
                data-automation={
                    isRefreshing
                        ? "signal-recommender-file-refresh-loading"
                        : "signal-recommender-file-download-loading"
                }
            >
                <CircularLoader options={DOWNLOAD_LOADER_OPTIONS} />
            </StyledDownloadButton>
        );
    }

    // A stalled run has nothing to download and the page has stopped polling it, so the only useful
    // action left is asking the server again.
    if (isStalled) {
        return (
            <Tooltip content={translate(signalRecommenderI18nKeys.list.refresh.tooltip)}>
                <StyledDownloadButton
                    type="button"
                    aria-label={translate(signalRecommenderI18nKeys.list.refresh.tooltip)}
                    aria-describedby={describedById}
                    onClick={onRefresh}
                    data-automation="signal-recommender-file-refresh"
                >
                    <Refresh size="sm" />
                </StyledDownloadButton>
            </Tooltip>
        );
    }

    const isInert = !isDealSignalSucceeded({ status });

    const handleClick = () => {
        if (isInert) {
            return;
        }

        onDownload();
    };

    return (
        <Tooltip
            content={translate(
                isInert
                    ? signalRecommenderI18nKeys.list.download.failedTooltip
                    : signalRecommenderI18nKeys.list.download.tooltip,
            )}
        >
            <StyledDownloadButton
                type="button"
                aria-label={translate(signalRecommenderI18nKeys.list.download.tooltip)}
                aria-describedby={describedById}
                aria-disabled={isInert}
                onClick={handleClick}
                data-automation="signal-recommender-file-download"
            >
                <Download size="sm" />
            </StyledDownloadButton>
        </Tooltip>
    );
};
